import {
  AxiosRequestConfigLoader,
  useLoaderStore,
} from '@core/store/LoaderStore';
import axios, {AxiosError, AxiosInstance} from 'axios';
import type {Composer} from 'vue-i18n';
import errorHandler from '@core/services/ErrorHandlerService';
import snackbarService from '@core/services/SnackbarService';
import Configuration from '@core/services/ConfigProvider';
import mgr from '@core/services/SecurityService';

/** vue-i18n translation function passed from app setup */
type I18nT = Composer['t'];

class HttpService {
  api: AxiosInstance;
  defaultAPI: AxiosInstance;
  /** BFF `/api/strapi/*`; Bearer set per request when OIDC session is valid (not via `setAccessToken`). */
  strapiBffApi: AxiosInstance;
  unauthorizedHandled = false;

  constructor() {
    const baseUrlFromConfiguration = Configuration.value('backendHost');

    this.api = axios.create({
      baseURL: `${baseUrlFromConfiguration}/api/`,
      showLoader: true,
    } as AxiosRequestConfigLoader);

    this.defaultAPI = axios.create({
      baseURL: `${baseUrlFromConfiguration}/`,
    });

    this.strapiBffApi = axios.create({
      baseURL: `${baseUrlFromConfiguration}/`,
    });

    let silentRenewPromise: Promise<unknown> | null = null;

    const attachAuthorizationHeader = async (
      config: Parameters<
        NonNullable<
          Parameters<AxiosInstance['interceptors']['request']['use']>[0]
        >
      >[0],
    ) => {
      try {
        let user = await mgr.getUser();

        if (user?.expired) {
          try {
            silentRenewPromise ??= mgr.signinSilent().finally(() => {
              silentRenewPromise = null;
            });
            user = (await silentRenewPromise) as typeof user;
          } catch {
            user = null;
          }
        }

        if (user?.access_token) {
          config.headers.Authorization = `Bearer ${user.access_token}`;
          this.setAccessToken(user.access_token);
        }
      } catch {
        // If anything fails reading user state, send without token
      }

      return config;
    };

    this.api.interceptors.request.use(attachAuthorizationHeader);
    this.strapiBffApi.interceptors.request.use(attachAuthorizationHeader);
  }

  setAccessToken(accessToken: string) {
    this.api.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    this.defaultAPI.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  }

  setupInterceptors(t: I18nT) {
    const loaderStore = useLoaderStore();

    this.api.interceptors.response.use(
      response => {
        if (
          (response && (response.config as AxiosRequestConfigLoader))
            ?.showLoader
        ) {
          loaderStore.endRequest();
        }
        return response;
      },
      error => {
        const {response} = error;
        if (
          (response && (response.config as AxiosRequestConfigLoader))
            ?.showLoader
        ) {
          loaderStore.endRequest();
        }
        return this.handleHttpError(error, t);
      },
    );

    this.api.interceptors.request.use(
      config => {
        if ((config as AxiosRequestConfigLoader)?.showLoader) {
          loaderStore.beginRequest();
        }
        return config;
      },
      error => {
        if ((error.config as AxiosRequestConfigLoader)?.showLoader) {
          loaderStore.endRequest();
        }

        if (error instanceof Error) {
          return Promise.reject(error);
        }

        const errorMessage =
          typeof error === 'string' ? error : JSON.stringify(error);
        return Promise.reject(new Error(errorMessage));
      },
    );
  }

  handleUnauthorizedError(userStore: {logout: () => void | Promise<void>}) {
    if (this.unauthorizedHandled) {
      return;
    }

    this.unauthorizedHandled = true;
    const redirectTarget = `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;
    userStore.logout();

    void mgr
      .removeUser()
      .catch(() => {})
      .finally(() => {
        if (
          globalThis.location.pathname.startsWith('/login') ||
          globalThis.location.pathname.startsWith('/callback')
        ) {
          return;
        }

        globalThis.location.assign(
          `/login?redirect=${encodeURIComponent(redirectTarget)}`,
        );
      });
  }

  async handleHttpError(error: AxiosError, t: I18nT) {
    const {useUserStore} = await import('@core/store/UserStore');
    const userStore = useUserStore();
    if (
      error.response?.status === 401 ||
      error.message === 'Request failed with status code 401'
    ) {
      this.handleUnauthorizedError(userStore);
      if (error instanceof Error) {
        throw error;
      }

      const errorMessage =
        typeof error === 'string' ? error : JSON.stringify(error);
      throw new Error(errorMessage);
    }

    const snackbarMessage = errorHandler.constructHttpErrorSnackbarMessage(
      error,
      t,
    );
    snackbarService.showSadSnackbar(snackbarMessage);

    if (error.response && error.response.status >= 500) {
      errorHandler.handleHttpError(error);
    }

    if (error instanceof Error) {
      throw error;
    }

    const finalErrorMessage =
      typeof error === 'string' ? error : JSON.stringify(error);
    throw new Error(finalErrorMessage);
  }
}

export default new HttpService();
